/**
 * useResetStaff2fa — TanStack mutation over
 * `POST /admin/staff/:userId/mfa/reset` (no body).
 *
 * Platform-staff MFA (2FA) reset trigger. Available to root + support
 * (`staff:reset_2fa` maps to both roles — see
 * `shared/auth/admin-permissions.ts`), subject to two server-side
 * guardrails: support may not target a root member (403), and no actor
 * may target themselves (422). Success is 204 No Content; the backend
 * deletes the target's MFA factors (force re-enroll), revokes all of
 * their sessions, and emails them.
 *
 * Like useResetStaffPassword this surfaces a structured RESULT rather
 * than throwing on the non-success branches — every outcome is a UI
 * state the caller renders inline (toast / aria-live announcement).
 *
 * Result union:
 *   - 'success'     — 204.
 *   - 'notFound'    — 404 (anti-enum; not a platform member).
 *   - 'rateLimited' — 429; carries `retryAfter` seconds parsed from the
 *                     `Retry-After` header (null when absent / unparseable).
 *   - 'forbidden'   — 403 (support attempted to target a root member).
 *   - 'cannotSelf'  — 422 (actor targeted themselves).
 *   - 'error'       — anything else.
 *
 * Does NOT invalidate the staff list — an MFA reset does not mutate any
 * staff-list field.
 */

import { useMutation } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';

export type ResetStaff2faResult =
  | { readonly kind: 'success' }
  | { readonly kind: 'notFound' }
  | { readonly kind: 'rateLimited'; readonly retryAfter: number | null }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'cannotSelf' }
  | { readonly kind: 'error' };

export interface UseResetStaff2faResult {
  readonly mutateAsync: (userId: string) => Promise<ResetStaff2faResult>;
  readonly isPending: boolean;
}

/** Parse a `Retry-After` header (seconds form) into a positive integer. */
function parseRetryAfter(headerValue: string | null): number | null {
  if (headerValue === null) {
    return null;
  }
  const seconds = Number.parseInt(headerValue, 10);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

export function useResetStaff2fa(): UseResetStaff2faResult {
  const mutation = useMutation<ResetStaff2faResult, never, string>({
    mutationFn: async (userId) => {
      const { response } = await apiClient.POST(
        '/admin/staff/{userId}/mfa/reset',
        { params: { path: { userId } } },
      );
      if (response.ok) {
        return { kind: 'success' };
      }
      if (response.status === 404) {
        return { kind: 'notFound' };
      }
      if (response.status === 429) {
        return {
          kind: 'rateLimited',
          retryAfter: parseRetryAfter(response.headers.get('Retry-After')),
        };
      }
      if (response.status === 403) {
        return { kind: 'forbidden' };
      }
      if (response.status === 422) {
        return { kind: 'cannotSelf' };
      }
      return { kind: 'error' };
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}
